import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getEnv } from "../../shared/lib/env";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { PageScaffold } from "../../shared/ui/PageScaffold";

export function LinkStudentPage() {
  const env = getEnv();
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const [searchParams] = useSearchParams();
  const { routeStatus, sessionStatus, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const inviteTokenFromQuery = searchParams.get("inviteToken")?.trim() ?? "";
  const [inviteToken, setInviteToken] = useState(
    inviteTokenFromQuery || (env.enableMock ? "demo-family-token" : "")
  );
  const [manualCode, setManualCode] = useState(env.enableMock ? "FAMILY-DEMO" : "");

  useEffect(() => {
    if (inviteTokenFromQuery) {
      setInviteToken(inviteTokenFromQuery);
    }
  }, [inviteTokenFromQuery]);

  return (
    <PageScaffold
      title="生徒との紐付け"
      description="招待URLまたは招待コードを入力して、生徒との紐付けを行います。"
      links={[
        { to: "/t/:tenantSlug/entry", label: "利用開始へ戻る" },
        { to: "/t/:tenantSlug/link-student/request", label: "申請する" }
      ]}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}

        {isSessionReady ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();

              if (!inviteToken.trim()) {
                return;
              }

              navigate(
                `/t/${tenantSlug}/link-student/confirm?inviteToken=${encodeURIComponent(inviteToken.trim())}`
              );
            }}
            style={{ display: "grid", gap: 8 }}
          >
            <label style={{ display: "grid", gap: 4 }}>
              <span>招待URLのトークン</span>
              <input
                value={inviteToken}
                onChange={(event) => setInviteToken(event.target.value)}
                placeholder="招待URLに含まれる token を入力"
              />
            </label>
            <button type="submit">招待URLを確認する</button>
          </form>
        ) : null}

        {isSessionReady ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();

              if (!manualCode.trim()) {
                return;
              }

              navigate(
                `/t/${tenantSlug}/link-student/confirm?manualCode=${encodeURIComponent(manualCode.trim())}`
              );
            }}
            style={{ display: "grid", gap: 8 }}
          >
            <label style={{ display: "grid", gap: 4 }}>
              <span>招待コード</span>
              <input
                value={manualCode}
                onChange={(event) => setManualCode(event.target.value)}
                placeholder="招待コードを入力"
              />
            </label>
            <button type="submit">招待コードを確認する</button>
          </form>
        ) : null}

        {isSessionReady && env.enableMock ? (
          <div style={{ fontSize: 14, opacity: 0.8 }}>
            <p style={{ margin: 0 }}>レビュー用サンプル値</p>
            <p style={{ margin: "4px 0 0" }}>家族向けトークン: `demo-family-token` / 家族向けコード: `FAMILY-DEMO`</p>
            <p style={{ margin: "4px 0 0" }}>生徒本人向けトークン: `demo-student-token` / 生徒本人向けコード: `ANNA-DEMO`</p>
          </div>
        ) : null}
      </div>
    </PageScaffold>
  );
}

export function NotFoundPage() {
  return (
    <PageScaffold title="ページが見つかりません" description="指定されたページは見つかりません。">
      <p>ルーティングを確認してください。</p>
    </PageScaffold>
  );
}

export function RootLandingPage() {
  return (
    <main style={{ maxWidth: 720, margin: "0 auto", padding: 16 }}>
      <section
        style={{
          padding: 24,
          border: "1px solid #e5e5e5",
          borderRadius: 16,
          background: "#fff",
          display: "grid",
          gap: 16
        }}
      >
        <div>
          <h1 style={{ margin: "0 0 12px" }}>MADAMUH LESSON</h1>
          <p style={{ margin: "0 0 8px" }}>クラブごとの専用URLからご利用ください。</p>
          <p style={{ margin: 0 }}>
            クラブから案内されたURL、またはLINE公式アカウントのメニューからアクセスしてください。
          </p>
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          <Link
            to="/t/demo-club"
            style={{
              display: "inline-block",
              width: "fit-content",
              padding: "10px 14px",
              border: "1px solid #ccc",
              borderRadius: 10,
              textDecoration: "none",
              background: "#fafafa"
            }}
          >
            デモを見る
          </Link>
          <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
            デモ画面では実際のクラブ運用とは異なるサンプルデータを表示します。
          </p>
        </div>
      </section>
    </main>
  );
}

export { ContactSettingsPage } from "./ContactSettingsPage";
export { ContractEmailChangeActionPage } from "./ContractEmailChangeActionPage";
export { ContractCancellationActionPage } from "./ContractCancellationActionPage";
export { ContractCancellationRevocationActionPage } from "./ContractCancellationRevocationActionPage";
export { EntryPage } from "./EntryPage";
export { LinkStudentConfirmPage } from "./LinkStudentConfirmPage";
export { LinkStudentRequestPage } from "./LinkStudentRequestPage";
export { MemberInvitePage } from "./MemberInvitePage";
export { ReviewDebugLineSubPage } from "./ReviewDebugLineSubPage";
export { StudentInvitePage } from "./StudentInvitePage";
export { SupportPage } from "./SupportPage";
export { TenantServiceEndedPage } from "./TenantServiceEndedPage";
